import React from "react";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronRight,
  Cloud,
  CreditCard,
  LayoutDashboard,
  MonitorSmartphone,
  Package,
  ReceiptText,
  ShieldCheck,
  ShoppingCart,
  UsersRound,
  Zap,
} from "lucide-react";

import FAQSection from "./FAQSection";

const products = [
  {
    icon: UsersRound,
    title: "OjMee HRMS",
    category: "Cloud HR & Workforce Management",

    short:
      "A cloud-based HRMS platform for managing employees, attendance, leave, payroll and everyday workforce operations.",

    description:
      "OjMee HRMS helps organizations centralize employee information, automate repetitive HR workflows and improve workforce visibility through one connected SaaS platform.",

    features: [
      "Employee Management",
      "Attendance & Leave",
      "Payroll Management",
      "HR Reports & Analytics",
    ],
  },

  {
    icon: ReceiptText,
    title: "OjMee Invoice",
    category: "GST Billing & Invoicing SaaS",

    short:
      "Cloud-based GST and non-GST invoicing software designed to simplify billing, customer management and payment tracking.",

    description:
      "OjMee Invoice enables businesses to create professional invoices, manage customers and products, track payments, prepare quotations and organize billing operations from one cloud platform.",

    features: [
      "GST & Non-GST Invoices",
      "Customer & Product Management",
      "Quotations & Payments",
      "Business Reports",
    ],
  },

  {
    icon: BarChart3,
    title: "OjMee CRM",
    category: "Customer Relationship Management",

    short:
      "A centralized CRM platform for managing leads, customers, sales pipelines, communication and follow-up activities.",

    description:
      "OjMee CRM helps businesses organize customer relationships, monitor sales opportunities and give teams better visibility across the complete customer journey.",

    features: [
      "Lead Management",
      "Customer Database",
      "Sales Pipeline",
      "Follow-Up Tracking",
    ],
  },

  {
    icon: MonitorSmartphone,
    title: "OjMee Orbit",
    category: "Remote Access & Support",

    short:
      "Secure remote access software designed to help teams connect with, manage and support computers from different locations.",

    description:
      "OjMee Orbit provides businesses and support teams with a connected environment for remote system access, technical assistance, device management and secure remote operations.",

    features: [
      "Remote Desktop Access",
      "Device Management",
      "Remote Support",
      "Secure Connectivity",
    ],
  },

  {
    icon: BriefcaseBusiness,
    title: "OjMee Business Suite",
    category: "Integrated Business Management",

    short:
      "A connected business software suite designed to bring important operational workflows and management tools into one ecosystem.",

    description:
      "OjMee Business Suite is designed for organizations that want to centralize business processes, teams, customers, transactions, reports and operational information.",

    features: [
      "Business Operations",
      "Team Management",
      "Workflow Management",
      "Reports & Analytics",
    ],
  },

  {
    icon: LayoutDashboard,
    title: "OjMee Dashboard",
    category: "Business Intelligence & Analytics",

    short:
      "Transform operational data into centralized dashboards, business KPIs, reports and actionable insights.",

    description:
      "OjMee Dashboard helps organizations monitor important business metrics and understand operational performance through visual reporting and connected analytics.",

    features: [
      "Business KPIs",
      "Visual Dashboards",
      "Operational Reports",
      "Performance Monitoring",
    ],
  },
];

const productSlider = [
  {
    name: "OjMee Invoice",
    logo: "/products/invoice-logo.png",
    url: "https://invoice.ojmee.in",
  },

  {
    name: "OjMee Connect",
    logo: "/products/connect-logo.png",
    url: "https://connect.ojmee.in",
  },

  {
    name: "OjMee Orbit",
    logo: "/products/orbit-logo.png",
    url: "https://orbit.ojmee.in",
  },

  {
    name: "OjMee CRM",
    logo: "/products/crm-logo.png",
    url: null,
  },

  {
    name: "OjMee HRMS",
    logo: "/products/hrms-logo.png",
    url: null,
  },

  {
    name: "OjMee Business Suite",
    logo: "/products/business-suite-logo.png",
    url: null,
  },
];

const benefits = [
  {
    icon: Cloud,
    title: "Cloud-Based SaaS",
    text: "Access modern business software through cloud-connected platforms designed for convenient and scalable operations.",
  },

  {
    icon: Zap,
    title: "Fast to Implement",
    text: "Start with a practical software foundation and configure workflows and modules around your business requirements.",
  },

  {
    icon: ShieldCheck,
    title: "Security Conscious",
    text: "Our products are designed with authentication, access controls, data protection and secure workflows in mind.",
  },

  {
    icon: Package,
    title: "Flexible & Scalable",
    text: "Add users, modules, integrations and capabilities as your organization and operational requirements grow.",
  },
];

const ecosystem = [
  {
    icon: UsersRound,
    title: "Manage People",
    text: "Manage employees, attendance, leave, payroll and workforce operations through HRMS.",
  },

  {
    icon: CreditCard,
    title: "Manage Transactions",
    text: "Handle invoices, quotations, customers, products, payments and billing workflows.",
  },

  {
    icon: ShoppingCart,
    title: "Manage Customers",
    text: "Organize leads, customer relationships, sales opportunities and commerce workflows.",
  },

  {
    icon: BarChart3,
    title: "Understand Business",
    text: "Turn operational information into dashboards, KPIs, reports and actionable insights.",
  },

  {
    icon: MonitorSmartphone,
    title: "Work Remotely",
    text: "Connect with remote computers and provide technical assistance through secure remote-access software.",
  },

  {
    icon: LayoutDashboard,
    title: "Connect Everything",
    text: "Build a connected software ecosystem around your organization's workflows and data.",
  },
];

function ProductLogoSlider() {
  return (
    <section className="relative w-full overflow-hidden border-y border-white/5 bg-white/[0.012] py-8 sm:py-10">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[180px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-500/[0.05] blur-[100px]" />

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6">
        <div className="mb-7 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-300/80">
            Explore OjMee Products
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4">
          {productSlider.map((product) => {
            const productCard = (
              <div
                className={`group flex h-[76px] min-w-[210px] items-center justify-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.025] px-5 transition duration-300 ${
                  product.url
                    ? "cursor-pointer hover:-translate-y-1 hover:border-pink-400/30 hover:bg-white/[0.055]"
                    : "hover:border-white/[0.13] hover:bg-white/[0.04]"
                }`}
              >
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/[0.07] bg-white p-0.5">
                  <img
                    src={product.logo}
                    alt={`${product.name} logo`}
                    loading="lazy"
                    className="h-full w-full object-contain"
                  />
                </div>

                <div className="flex min-w-0 flex-col justify-center">
                  <p className="whitespace-nowrap text-sm font-black text-white">
                    {product.name}
                  </p>

                  <div className="mt-1 flex items-center gap-1">
                    <span className="whitespace-nowrap text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-600 transition group-hover:text-pink-300/70">
                      {product.url ? "Explore Product" : "OjMee Product"}
                    </span>

                    {product.url && (
                      <ArrowRight className="h-3 w-3 text-pink-400/60 transition duration-300 group-hover:translate-x-1 group-hover:text-pink-300" />
                    )}
                  </div>
                </div>
              </div>
            );

            if (product.url) {
              return (
                <a
                  key={product.name}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${product.name}`}
                  className="shrink-0"
                >
                  {productCard}
                </a>
              );
            }

            return (
              <div key={product.name} className="shrink-0">
                {productCard}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Products() {
  return (
    <main className="w-full overflow-hidden">
      <section className="relative w-full px-4 py-5 sm:px-6 lg:py-10">
        <div className="relative w-full px-4">
          <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[600px] w-[800px] -translate-x-1/2 rounded-full bg-pink-600/10 blur-[160px]" />

          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-5xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-5 py-2.5">
                <Cloud className="h-4 w-4 text-pink-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-200">
                  OjMee SaaS Products
                </span>
              </div>

              {/* H1 */}

              <h1 className="text-4xl font-black leading-tight sm:text-6xl lg:text-7xl">
                SaaS & Business Software
                <br />
                <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                  Built for Modern Operations
                </span>
              </h1>

              <p className="mx-auto mt-7 max-w-4xl text-base leading-8 text-slate-400 sm:text-lg">
                OjMee Technology builds cloud-based SaaS products that help
                businesses manage employees, customers, billing, sales,
                reporting, remote operations and everyday workflows through
                connected digital platforms.
              </p>

              <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-slate-500 sm:text-base">
                Start with the software your organization needs today and expand
                into a connected OjMee ecosystem as your teams, customers, data
                and operational requirements grow.
              </p>

              <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-7 py-4 font-bold text-white shadow-xl shadow-pink-500/20 transition hover:scale-105"
                >
                  Request Product Demo
                  <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-4 font-bold text-slate-200 transition hover:border-pink-400/30 hover:bg-white/10"
                >
                  Explore SaaS Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-20">
        <div className="pointer-events-none absolute right-0 top-1/3 -z-10 h-[500px] w-[500px] rounded-full bg-violet-600/10 blur-[150px]" />

        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">
              <Package className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                Our SaaS Ecosystem
              </span>
            </div>

            <h2 className="text-3xl font-black sm:text-5xl">
              Business Software That Solves
              <br />
              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                Real Operational Problems
              </span>
            </h2>

            <p className="mt-5 text-sm leading-8 text-slate-500 sm:text-base">
              Each OjMee product is designed around practical business workflows
              to reduce repetitive work, centralize information, improve
              visibility and make everyday operations easier to manage.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {products.map((product, index) => {
              const Icon = product.icon;

              return (
                <article
                  key={product.title}
                  className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.025] p-7 transition duration-500 hover:-translate-y-2 hover:border-pink-400/30 hover:bg-white/[0.045]"
                >
                  <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-pink-500/10 opacity-0 blur-[80px] transition duration-500 group-hover:opacity-100" />

                  <div className="relative">
                    <div className="flex items-start justify-between">
                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15 ring-1 ring-white/5">
                        <Icon className="h-6 w-6 text-pink-300" />
                      </div>

                      <span className="text-xs font-bold tracking-widest text-slate-700">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>

                    <div className="mt-6">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-pink-300/70">
                        {product.category}
                      </span>

                      <h3 className="mt-2 text-2xl font-black">
                        {product.title}
                      </h3>
                    </div>

                    <p className="mt-4 text-sm font-medium leading-7 text-slate-300">
                      {product.short}
                    </p>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {product.description}
                    </p>

                    <div className="mt-6 space-y-2.5 border-t border-white/10 pt-5">
                      {product.features.map((feature) => (
                        <div
                          key={feature}
                          className="flex items-center gap-2 text-xs text-slate-400"
                        >
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-pink-400" />

                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to="/contact"
                      className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-pink-300 transition group-hover:text-pink-200"
                    >
                      Request Product Information
                      <ChevronRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <ProductLogoSlider />

      <section className="w-full px-4 py-20 sm:px-6 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">
                <Zap className="h-4 w-4 text-pink-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                  Built for Business Growth
                </span>
              </div>

              <h2 className="text-3xl font-black sm:text-5xl">
                SaaS Technology That
                <br />
                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  Grows With Your Business
                </span>
              </h2>

              <p className="mt-6 text-sm leading-8 text-slate-500 sm:text-base">
                Business requirements change as organizations add more
                customers, employees, locations, transactions and operational
                processes. Our SaaS products are designed with that evolution in
                mind.
              </p>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">
                Start with the capabilities your business needs today and expand
                your software environment through additional users, modules,
                workflows, integrations and connected OjMee products.
              </p>

              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-[#10091f] transition hover:bg-pink-100"
              >
                Talk to Our SaaS Team
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {benefits.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-400/30"
                  >
                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/10 to-violet-500/10">
                      <Icon className="h-5 w-5 text-violet-300" />
                    </div>

                    <h3 className="mt-5 text-lg font-black">{item.title}</h3>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">
              <LayoutDashboard className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                Connected SaaS Ecosystem
              </span>
            </div>

            <h2 className="text-3xl font-black sm:text-5xl">
              One Technology Company.
              <br />
              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                A Connected Business Ecosystem.
              </span>
            </h2>

            <p className="mt-5 text-sm leading-8 text-slate-500 sm:text-base">
              Use individual OjMee SaaS products for specific business
              requirements or combine multiple solutions to create a more
              connected software environment across your organization.
            </p>
          </div>

          <div className="relative mt-14">
            <div className="hidden h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent lg:block" />

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {ecosystem.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-3xl border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-pink-400/25"
                  >
                    <div className="flex items-start gap-4">
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/5">
                        <Icon className="h-5 w-5 text-pink-300" />
                      </div>

                      <div>
                        <h3 className="font-black">{item.title}</h3>

                        <p className="mt-1 text-xs leading-6 text-slate-500">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full px-4 py-20 sm:px-6 lg:py-24">
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[450px] w-[850px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/[0.07] blur-[160px]" />

        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-[2.5rem] border border-white/10 bg-white/[0.025] p-7 sm:p-10">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-pink-500/10">
                <Package className="h-6 w-6 text-pink-300" />
              </div>

              <h2 className="mt-6 text-2xl font-black sm:text-3xl">
                Need Product Customization?
              </h2>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">
                Every organization has different workflows. OjMee software can
                be extended with business-specific modules, reports, workflows
                and functionality depending on project requirements.
              </p>

              <Link
                to="/contact"
                className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-pink-300"
              >
                Discuss Customization
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="rounded-[2.5rem] border border-white/10 bg-white/[0.025] p-7 sm:p-10">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-violet-500/10">
                <Cloud className="h-6 w-6 text-violet-300" />
              </div>

              <h2 className="mt-6 text-2xl font-black sm:text-3xl">
                Connect With Existing Systems
              </h2>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">
                Connect OjMee products with APIs, databases, payment systems,
                communication platforms and other business applications to
                create more efficient digital workflows.
              </p>

              <Link
                to="/services"
                className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-violet-300"
              >
                Explore Integration Services
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-pink-500/[0.07] via-white/[0.025] to-violet-500/[0.07] px-6 py-12 text-center sm:px-10 lg:px-16 lg:py-16">
            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-pink-500/10 blur-[100px]" />

            <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-violet-500/10 blur-[100px]" />

            <div className="relative">
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-300">
                Build With OjMee Technology
              </span>

              <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-black leading-tight sm:text-5xl">
                Ready to Simplify Your Business
                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  {" "}
                  With Better Software?
                </span>
              </h2>

              <p className="mx-auto mt-6 max-w-2xl text-sm leading-8 text-slate-400 sm:text-base">
                Explore an OjMee SaaS product, request a product demo or discuss
                how our software can be configured and integrated around your
                organization's operational requirements.
              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-7 py-4 text-sm font-bold text-white shadow-xl shadow-pink-500/20 transition hover:scale-105"
                >
                  Request a Demo
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-4 text-sm font-bold text-slate-200 transition hover:border-pink-400/30 hover:bg-white/10"
                >
                  Explore Our Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full px-4 py-20 sm:px-6 lg:py-0">
        <FAQSection />
      </section>
    </main>
  );
}
